import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import ReviewCard from '@/components/base/ReviewCard';
import { comics } from '@/mocks/comics';
import { reviews } from '@/mocks/reviews';
import { type FC } from 'react';

const ComicPage: FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-foreground-500">漫画未找到</p>
      </div>
    );
  }

  const relatedComics = comics.filter((c) => c.id !== comic.id).slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'http://chinahangnuo.com/' },
          { '@type': 'ListItem', position: 2, name: `${comic.category}漫画`, item: `http://chinahangnuo.com/category/${comic.category === '热血' ? 'action' : comic.category === '恋爱' ? 'romance' : comic.category === '奇幻' ? 'fantasy' : comic.category === '悬疑' ? 'mystery' : 'comedy'}` },
          { '@type': 'ListItem', position: 3, name: comic.title, item: `http://chinahangnuo.com/comic/${slug}` },
        ],
      },
      {
        '@type': 'Article',
        headline: `${comic.title} - 韩国漫画免费在线阅读 - 韩漫画网`,
        url: `http://chinahangnuo.com/comic/${slug}`,
        description: comic.description,
        author: { '@type': 'Person', name: comic.author },
        image: comic.cover,
        datePublished: '2026-01-01',
        dateModified: comic.updatedAt,
        publisher: { '@type': 'Organization', name: '韩漫画网', url: 'http://chinahangnuo.com/' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: `${comic.title}更新到多少话了？`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `${comic.title}目前已更新至${comic.chapters}话，状态为${comic.status}。韩漫画网每日同步更新，确保您第一时间阅读最新内容。`,
            },
          },
          {
            '@type': 'Question',
            name: `${comic.title}好看吗？评分多少？`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `${comic.title}在韩漫画网上获得了${comic.rating}分的高分评价，深受读者喜爱。${comic.description}`,
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${comic.author} - 韩国漫画免费在线阅读 - 韩漫画网`}
        description={`韩漫画网提供${comic.title}免费在线阅读。${comic.description}作者：${comic.author}，状态：${comic.status}，已更新${comic.chapters}话。`}
        keywords={`${comic.title},${comic.author},韩国漫画,免费漫画,${comic.category}漫画,韩漫`}
        canonicalUrl={`http://chinahangnuo.com/comic/${slug}`}
        ogType="article"
        ogImage={comic.cover}
        schema={articleSchema}
      />

      <Breadcrumb
        items={[
          { name: '首页', path: '/' },
          { name: `${comic.category}漫画`, path: `/category/${comic.category === '热血' ? 'action' : comic.category === '恋爱' ? 'romance' : comic.category === '奇幻' ? 'fantasy' : comic.category === '悬疑' ? 'mystery' : 'comedy'}` },
          { name: comic.title },
        ]}
      />

      <section className="flex flex-col lg:flex-row">
        <div className="lg:w-1/2 bg-background-100 flex items-center justify-center p-6 md:p-10">
          <div className="max-w-sm w-full">
            <div className="relative aspect-[3/4] overflow-hidden rounded-lg">
              <img
                src={comic.cover}
                alt={`${comic.title} - 韩国漫画`}
                title={`${comic.title} - ${comic.author} - 韩漫画网`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>

        <div className="lg:w-1/2 flex items-center px-6 md:px-12 py-8 md:py-12 bg-background-50">
          <div className="max-w-lg">
            <div className="flex items-center gap-2 mb-3">
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${
                comic.status === '连载中'
                  ? 'bg-accent-100 text-accent-700'
                  : 'bg-secondary-100 text-secondary-700'
              }`}>
                {comic.status}
              </span>
              <span className="text-xs text-foreground-500">{comic.category}</span>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
              {comic.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mb-5 text-sm">
              <span className="text-foreground-600">
                <strong className="text-foreground-800">作者：</strong>{comic.author}
              </span>
              <span className="text-accent-500 font-semibold">{comic.rating} ★</span>
              <span className="text-foreground-500">{comic.chapters}话</span>
              <span className="text-foreground-400">{comic.views.toLocaleString()} 次阅读</span>
            </div>

            <p className="text-sm text-foreground-600 leading-relaxed mb-5">
              {comic.description}
            </p>

            <div className="flex flex-wrap gap-1.5 mb-6">
              {comic.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button className="bg-accent-500 text-background-50 px-8 py-3 rounded-full text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors duration-200 cursor-pointer flex items-center justify-center gap-2">
                开始阅读
                <i className="ri-arrow-right-line"></i>
              </button>
              <button className="border border-background-300 text-foreground-700 px-8 py-3 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors duration-200 cursor-pointer flex items-center justify-center gap-2">
                <i className="ri-bookmark-line"></i>
                加入收藏
              </button>
            </div>

            <div className="mt-6 text-xs text-foreground-500">
              最后更新：{comic.updatedAt}
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900">
                相关推荐
              </h2>
              <p className="text-sm text-foreground-500 mt-1">你可能也会喜欢这些漫画</p>
            </div>
            <Link
              to={`/category/${comic.category === '热血' ? 'action' : comic.category === '恋爱' ? 'romance' : comic.category === '奇幻' ? 'fantasy' : comic.category === '悬疑' ? 'mystery' : 'comedy'}`}
              className="text-sm text-accent-500 hover:text-accent-600 transition-colors duration-200 whitespace-nowrap flex items-center gap-1"
            >
              查看更多
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {relatedComics.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-accent-500"></span>
              <span className="text-xs font-medium text-accent-600 tracking-wider">读者评论</span>
            </div>
            <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900">
              读者热议
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {reviews.slice(0, 3).map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ComicPage;